<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Record" }} Delivery</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Delivery Date/Time *</label>
              <input type="datetime-local" v-model="form.delivery_datetime" required class="form-control" />
            </div>

            <div class="form-group">
              <label>Mode of Delivery *</label>
              <select v-model="form.mode_of_delivery" required class="form-control">
                <option value="">Select</option>
                <option value="SVD">Spontaneous Vaginal Delivery (SVD)</option>
                <option value="Vacuum">Vacuum Assisted</option>
                <option value="Forceps">Forceps Assisted</option>
                <option value="Elective C-Section">Elective C-Section</option>
                <option value="Emergency C-Section">Emergency C-Section</option>
                <option value="Breech">Breech</option>
              </select>
            </div>

            <div class="form-group">
              <label>Number of Babies</label>
              <input type="number" v-model="form.number_of_babies" min="1" class="form-control" />
            </div>

            <div class="form-group full-width">
              <label>Indication (if instrumental/operative)</label>
              <input type="text" v-model="form.indication" class="form-control" />
            </div>

            <div class="form-group">
              <label>Stage 1 Duration (min)</label>
              <input type="number" v-model="form.stage1_duration_minutes" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Stage 2 Duration (min)</label>
              <input type="number" v-model="form.stage2_duration_minutes" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Stage 3 Duration (min)</label>
              <input type="number" v-model="form.stage3_duration_minutes" min="0" class="form-control" />
            </div>

            <div class="form-group">
              <label>Placenta Delivery Method</label>
              <select v-model="form.placenta_delivery_method" class="form-control">
                <option value="">Select</option>
                <option value="Spontaneous">Spontaneous</option>
                <option value="Controlled cord traction">Controlled cord traction</option>
                <option value="Manual removal">Manual removal</option>
              </select>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.placenta_complete" />
                Placenta complete
              </label>
            </div>

            <div class="form-group">
              <label>Perineum Status</label>
              <select v-model="form.perineum_status" class="form-control">
                <option value="">Select</option>
                <option value="Intact">Intact</option>
                <option value="Episiotomy">Episiotomy</option>
                <option value="1st degree tear">1st degree tear</option>
                <option value="2nd degree tear">2nd degree tear</option>
                <option value="3rd degree tear">3rd degree tear</option>
                <option value="4th degree tear">4th degree tear</option>
              </select>
            </div>

            <div class="form-group">
              <label>Estimated Blood Loss (ml)</label>
              <input type="number" v-model="form.estimated_blood_loss_ml" min="0" class="form-control" />
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.uterotonic_given" />
                Uterotonic given
              </label>
            </div>
            <div class="form-group" v-if="form.uterotonic_given">
              <label>Uterotonic Used</label>
              <input type="text" v-model="form.uterotonic_used" placeholder="e.g. Oxytocin 10IU IM" class="form-control" />
            </div>

            <div class="form-group full-width">
              <label>Complications</label>
              <textarea v-model="form.complications" class="form-control" rows="2"></textarea>
            </div>

            <div class="form-group">
              <label>Conducted By</label>
              <input type="text" v-model="form.conducted_by" class="form-control" />
            </div>
            <div class="form-group">
              <label>Assisted By</label>
              <input type="text" v-model="form.assisted_by" class="form-control" />
            </div>

            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="form.notes" class="form-control" rows="3"></textarea>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : (editingRecord ? "Update" : "Save") + " Delivery" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "DeliveryModal",
  props: {
    visitId: { type: [Number, String], default: null },
    editRecord: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        ...this.emptyForm(),
        ...this.editRecord,
        delivery_datetime: this.toLocalDateTime(this.editRecord.delivery_datetime),
      };
    }
  },
  methods: {
    emptyForm() {
      return {
        delivery_datetime: this.toLocalDateTime(new Date()),
        mode_of_delivery: "",
        number_of_babies: 1,
        indication: "",
        stage1_duration_minutes: "",
        stage2_duration_minutes: "",
        stage3_duration_minutes: "",
        placenta_delivery_method: "",
        placenta_complete: true,
        perineum_status: "",
        estimated_blood_loss_ml: "",
        uterotonic_given: false,
        uterotonic_used: "",
        complications: "",
        conducted_by: "",
        assisted_by: "",
        notes: "",
      };
    },
    toLocalDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    closeModal() {
      this.$emit("close");
    },
    async saveRecord() {
      this.saving = true;
      try {
        const payload = { ...this.form, visit_id: this.visitId };
        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(`deliveries/${this.editingRecord.id}`, "PUT", payload, 1);
        } else {
          response = await generalApiRequest("deliveries", "POST", payload, 1);
        }
        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save delivery record");
        }
      } catch (error) {
        console.error("Error saving delivery record:", error);
        alert("An error occurred while saving the delivery record");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.mmodal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
.modal-header { padding: 20px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 20px; }
.close { background: rgb(233, 22, 22); border: 1px solid rgb(182, 178, 178); font-size: 1.5rem; color: white; line-height: 1; cursor: pointer; }
.modal-body { padding: 30px; overflow-y: auto; flex-grow: 1; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 15px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; }
.checkbox-label { display: flex !important; align-items: center; gap: 8px; margin-top: 28px; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 15px; }
.btn { padding: 10px 25px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
.btn-secondary { background: #6c757d; color: white; }
.btn-secondary:hover { background: #5a6268; }
.btn-primary { background: #667eea; color: white; }
.btn-primary:hover { background: #5a6fd8; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 1200px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
